import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { roleHome } from "../components/ProtectedRoute";
import { Truck, CircleNotch, User, SteeringWheel } from "@phosphor-icons/react";
import { REGION_NAMES, comunasOf } from "../lib/chile";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "CARRIER" ? "CARRIER" : "CLIENT");
  const [form, setForm] = useState({ name: "", lastName: "", rut: "", email: "", phone: "", region: "", city: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Debes aceptar los Términos y la Política de Privacidad");
      return;
    }
    setLoading(true);
    const res = await register({ ...form, role, acceptedTerms: true });
    setLoading(false);
    if (res.ok) {
      toast.success("¡Cuenta creada!");
      navigate(roleHome(res.user.role));
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA] flets-grain">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <img src="/frets-logo.png" alt="Frets Transporte" className="h-10 w-auto" />
        </Link>

        <div className="bg-white rounded-3xl border border-zinc-200 shadow-xl p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold tracking-tight text-center">Crea tu cuenta</h2>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button type="button" onClick={() => setRole("CLIENT")} data-testid="role-client"
              className={`rounded-2xl border p-4 text-left transition-all ${role === "CLIENT" ? "border-[#FF5A36] bg-[#FFF4F1] ring-2 ring-[#FF5A36]" : "border-zinc-200 hover:border-zinc-300"}`}>
              <User size={24} weight="bold" className={role === "CLIENT" ? "text-[#FF5A36]" : "text-zinc-400"} />
              <div className="font-semibold mt-2">Cliente</div>
              <div className="text-xs text-zinc-500">Necesito transportar</div>
            </button>
            <button type="button" onClick={() => setRole("CARRIER")} data-testid="role-carrier"
              className={`rounded-2xl border p-4 text-left transition-all ${role === "CARRIER" ? "border-[#FF5A36] bg-[#FFF4F1] ring-2 ring-[#FF5A36]" : "border-zinc-200 hover:border-zinc-300"}`}>
              <SteeringWheel size={24} weight="bold" className={role === "CARRIER" ? "text-[#FF5A36]" : "text-zinc-400"} />
              <div className="font-semibold mt-2">Transportista</div>
              <div className="text-xs text-zinc-500">Presto servicios</div>
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre" testid="reg-name" value={form.name} onChange={set("name")} required />
              <Field label="Apellido" testid="reg-lastname" value={form.lastName} onChange={set("lastName")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RUT" testid="reg-rut" value={form.rut} onChange={set("rut")} placeholder="12.345.678-9" />
              <Field label="Teléfono" testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+56 9 ..." />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Región</label>
              <select data-testid="reg-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value, city: "" })}
                className="mt-1 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                <option value="">Selecciona tu región</option>
                {REGION_NAMES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Comuna / Ciudad</label>
              <select data-testid="reg-city" value={form.city} disabled={!form.region} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="mt-1 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:opacity-50">
                <option value="">{form.region ? "Selecciona tu comuna" : "Primero elige la región"}</option>
                {comunasOf(form.region).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Email" testid="reg-email" type="email" value={form.email} onChange={set("email")} required />
            <Field label="Contraseña" testid="reg-password" type="password" value={form.password} onChange={set("password")} required placeholder="Mínimo 6 caracteres" />

            <label className="flex items-start gap-2.5 pt-1 cursor-pointer" data-testid="reg-terms-label">
              <input type="checkbox" data-testid="reg-terms" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#FF5A36] focus:ring-[#FF5A36]" />
              <span className="text-xs text-zinc-600 leading-relaxed">
                He leído y acepto los{" "}
                <Link to="/legal/terminos" target="_blank" className="font-semibold text-[#FF5A36] hover:underline">Términos y Condiciones</Link>{" "}y la{" "}
                <Link to="/legal/privacidad" target="_blank" className="font-semibold text-[#FF5A36] hover:underline">Política de Privacidad</Link>{" "}
                (Ley N° 21.719).
              </span>
            </label>

            <button data-testid="register-submit" disabled={loading || !accepted}
              className="w-full py-3.5 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
              {loading ? <CircleNotch size={20} className="animate-spin" /> : "Crear cuenta"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-zinc-600">
            ¿Ya tienes cuenta? <Link to="/login" className="font-semibold text-[#FF5A36] hover:underline">Ingresa</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} {...props}
        className="mt-1 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] focus:border-transparent" />
    </div>
  );
}
